import { cn } from '@/lib/cn'

export interface UpstreamSection {
  id: number
  name: string
}

/**
 * Which sections a flagger also tells when they raise an issue. Only the
 * sections this job already passed through are offered, and the one right
 * before theirs is ticked when the dialog opens.
 *
 * "All previous" is expanded to ids here rather than sent as a token, so the
 * log line records the sections that were really told even if the flow is
 * edited later. The server re-checks every id against the job's own flow, so
 * this is a convenience, not the gate.
 *
 * Chips rather than a multi-select: there are at most a handful of sections,
 * the flagger is choosing who to wake up, and every option and its state has
 * to be visible at once. The recipe is HelperPicker's, so the two audience
 * pickers on this screen read as one idea.
 */
export default function UpstreamSectionPicker({
  sections,
  value,
  onChange,
  disabled,
}: {
  /** Previous sections in flow order, from GET /staff/scan. */
  sections: UpstreamSection[]
  value: number[]
  onChange: (ids: number[]) => void
  disabled?: boolean
}) {
  // The first section on a flow has nothing behind it, so there is nothing
  // to offer and the dialog stays as it was.
  if (sections.length === 0) return null

  const all = value.length === sections.length
  const toggle = (id: number) =>
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id])

  const chip = (on: boolean) =>
    cn(
      'rounded-lg border px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-40',
      on ? 'border-primary/60 bg-primary/20 text-primary' : 'border-line text-muted hover:bg-foreground/5',
    )

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-foreground/60">
        Also notify a previous section (optional)
      </p>

      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {/* With one previous section this would just be a second copy of it. */}
        {sections.length > 1 && (
          <button
            type="button"
            aria-pressed={all}
            disabled={disabled}
            onClick={() => onChange(all ? [] : sections.map((s) => s.id))}
            className={chip(all)}
          >
            All previous
          </button>
        )}
        {sections.map((section) => (
          <button
            key={section.id}
            type="button"
            aria-pressed={value.includes(section.id)}
            disabled={disabled}
            onClick={() => toggle(section.id)}
            className={chip(value.includes(section.id))}
          >
            {section.name}
          </button>
        ))}
      </div>

      <p className="mt-1.5 text-xs text-muted">
        They can open the job, see the problem, and mark the issue resolved. Only your section can update the job.
      </p>
    </div>
  )
}
